/**
 * Public air traffic around the map view (phase 2b.4 T6, T7; Diurnal parity 03-013 to 03-017; docs/specs/tracker.md
 * §5.2): other aircraft from community ADS-B, OpenSky and the electronic flight bags, shown with the organisation's
 * own aircraft (one icon per aircraft, `onePerAircraft`).
 *
 * Who fetches (03-017, decision 65): each laptop, around its own map view, as the Diurnal tool does; positions never
 * go through Teslyn Cloud, so every laptop uses its own allowance. Teslyn Cloud's own polling per project area, for
 * its Operations Hub map, is `cloud-traffic.ts` (parity C7, decision 627).
 * Teslyn Field asks through its own fixed requests (`public_traffic_fetch`), with its own name as the user agent:
 * never a browser's, and never a Referer or Origin of another site.
 *
 * The sources (terms read 20 Oct 2026, spec §5.2): Community ADS-B from adsb.lol (ODbL, free, credited on the map;
 * every 300 s, decision 356), with adsb.fi as its fallback as in the Diurnal tool (decision 355; MB confirms an
 * arrangement with adsb.fi before other users have it); Oz Runways and AvPlan (decision 65); OpenSky with the
 * organisation's own API client (decision 354; MB secures OpenSky's permission before anyone else uses it). OpenSky's
 * client id and secret stay in Teslyn Cloud (Vault): the provider proxy exchanges them for a 30-minute access token,
 * and this laptop asks OpenSky itself with that token, so the secret never reaches a laptop and the positions never
 * pass through Teslyn's server.
 *
 * Two map windows (decision 378, Q301 answer B): only the map window being looked at asks the services; the other
 * windows show the last answer they had (`active_window`, `windowAsks`). `sharedTrafficRequest` (answer C, one
 * request covering several windows) stays available but unused.
 *
 * No timers or network of its own: the tracker feed calls `poll()` on its timer with the app's fetcher.
 */
import type { GeoBounds } from './map-geo.js';
import {
  TRACKER_SOURCE_NAME,
  kindFromIcaoType,
  normaliseHex,
  validFix,
  type PublicTrafficId,
  type TrackPoint,
  type TrackedPosition,
} from './tracker.js';
import type { Parsed } from './tracker-adapters.js';
import { BUSHFIRE_IO_ON, BUSHFIRE_POLL_MS, parseBushfireAircraft } from './bushfire-io.js';

/** A second service asked when the first cannot answer (Community ADS-B: adsb.fi after adsb.lol, am:7960–8050). */
export interface PublicTrafficFallback {
  /** The request's `via`. */
  readonly via: 'adsbfi';
  readonly name: string;
  readonly credit: string;
  /** The least time between two requests to it from this laptop (adsb.fi allows one a second). */
  readonly minSpacingMs: number;
}

/** What Teslyn knows of one public traffic source. */
export interface PublicTrafficSource {
  readonly id: PublicTrafficId;
  readonly name: string;
  /** The service asked first, as messages name it ("adsb.lol" for Community ADS-B). */
  readonly serviceName: string;
  /** Offered only when the service's terms allow Teslyn's use (spec §5.2). */
  readonly offered: boolean;
  /** Why it is not offered, in words; null when it is. */
  readonly why: string | null;
  /** What is asked: a radius around the map centre, the whole world (filtered here), or the map view as a box. */
  readonly area: 'radius' | 'world' | 'view_box';
  /** Needs the organisation's own account in Teslyn Cloud, Integrations (OpenSky's API client). */
  readonly needsAccount: boolean;
  /** Poll interval. */
  readonly pollMs: number;
  /** The least time between two requests, also after the map moves. */
  readonly minGapMs: number;
  /** A map move asks again after this long (the Diurnal debounce). Zero: the feed covers the world already. */
  readonly moveDebounceMs: number;
  /** How far around the map centre, nautical miles: the default and the most the service (or Teslyn) allows. */
  readonly radiusNm: { readonly default: number; readonly max: number };
  /** The wait after a 429 or 403 without the service's own Retry-After: doubling from `firstMs` up to `maxMs`. */
  readonly backoff: { readonly firstMs: number; readonly maxMs: number };
  /** Trail kept per aircraft, hours (the Diurnal stores, am:467–468). */
  readonly trailHours: number;
  /** Positions older than this are dropped (Oz Runways and AvPlan grey markers kept up to 12 h, am:469). */
  readonly maxAgeMs: number;
  /** The credit the service asks for, shown on the map. */
  readonly credit: string;
  /** Asked when the first service cannot answer; none for most sources. */
  readonly fallback: PublicTrafficFallback | null;
}

const MIN = 60_000;
const HOUR = 3_600_000;
/** The Diurnal tool's wait after a 429 or 403 of Community ADS-B, Oz Runways and AvPlan (am:8023–8037). */
const BACKOFF_MINUTES = { firstMs: MIN, maxMs: 15 * MIN } as const;

/** OpenSky's largest box for one credit (square degrees), and the box asked when the view is larger (am:5921–5935). */
export const OPENSKY_ONE_CREDIT_SQ_DEG = 25;
export const OPENSKY_CLAMP_DEG = 5;

export const PUBLIC_TRAFFIC_SOURCES: Readonly<Record<PublicTrafficId, PublicTrafficSource>> = {
  adsb: {
    id: 'adsb',
    name: TRACKER_SOURCE_NAME.adsb,
    serviceName: 'adsb.lol',
    offered: true,
    why: null,
    area: 'radius',
    needsAccount: false,
    // adsb.lol allows 4 requests a second, 60 a minute and 500 a day to one computer (its x-ratelimit-limit header,
    // 20 Oct 2026): one request every 300 seconds is 288 in a whole day (decision 356; the Diurnal tool's 30 s used
    // the 500 up in about 4 hours)
    pollMs: 300_000,
    minGapMs: 30_000,
    moveDebounceMs: 3_000,
    // adsb.lol and adsb.fi answer at most 250 nm: the limit shown is the limit used (bug 03-13)
    radiusNm: { default: 100, max: 250 },
    backoff: BACKOFF_MINUTES,
    trailHours: 6,
    maxAgeMs: 2 * MIN,
    credit: 'Traffic: adsb.lol (ODbL)',
    // decision 355: adsb.fi when adsb.lol cannot answer, as the Diurnal tool. Its open data asks for a citation with
    // a link to its home page, and allows one request a second (github.com/adsbfi/opendata, read 20 Oct 2026)
    fallback: {
      via: 'adsbfi',
      name: 'adsb.fi',
      credit: 'Traffic: adsb.fi (https://adsb.fi)',
      minSpacingMs: 1_000,
    },
  },
  ozrunways: {
    id: 'ozrunways',
    name: TRACKER_SOURCE_NAME.ozrunways,
    serviceName: TRACKER_SOURCE_NAME.ozrunways,
    offered: true,
    why: null,
    area: 'world',
    needsAccount: false,
    pollMs: 30_000,
    minGapMs: 15_000,
    // the feed covers the world: a map move filters the last answer again without asking
    moveDebounceMs: 0,
    radiusNm: { default: 250, max: 500 },
    backoff: BACKOFF_MINUTES,
    trailHours: 12,
    maxAgeMs: 12 * HOUR,
    credit: 'Traffic: OzRunways',
    fallback: null,
  },
  avplan: {
    id: 'avplan',
    name: TRACKER_SOURCE_NAME.avplan,
    serviceName: TRACKER_SOURCE_NAME.avplan,
    offered: true,
    why: null,
    area: 'world',
    needsAccount: false,
    pollMs: 30_000,
    minGapMs: 15_000,
    moveDebounceMs: 0,
    radiusNm: { default: 250, max: 500 },
    backoff: BACKOFF_MINUTES,
    trailHours: 12,
    maxAgeMs: 12 * HOUR,
    credit: 'Traffic: AvPlan EFB',
    fallback: null,
  },
  opensky: {
    // decision 354: the organisation's own OpenSky API client (OAuth2 client credentials; basic auth is no longer
    // accepted), asked as the Diurnal tool asked: the map view every 180 s, again 5 s after the map moves, the box
    // clamped to 5 x 5 degrees when the view is over 25 square degrees (one credit a request), and 30 s doubling to
    // 240 s after a 429 (am:5895–6036). An OpenSky account has 4,000 credits a day.
    id: 'opensky',
    name: TRACKER_SOURCE_NAME.opensky,
    serviceName: TRACKER_SOURCE_NAME.opensky,
    offered: true,
    why: null,
    area: 'view_box',
    needsAccount: true,
    pollMs: 180_000,
    minGapMs: 30_000,
    moveDebounceMs: 5_000,
    radiusNm: { default: 0, max: 0 },
    backoff: { firstMs: 30_000, maxMs: 240_000 },
    trailHours: 6,
    maxAgeMs: 2 * MIN,
    credit: 'Traffic: The OpenSky Network',
    fallback: null,
  },
  bushfireio: {
    // decision 476: bushfire.io's fire aircraft, for Thomson Airborne only (its organisation switch) while MB asks
    // Disaster Science; its terms allow personal, non-commercial use only without an agreement (docs/specs/map.md
    // §31). The tiles of the map view (at most four), every minute, as they are regenerated; positions come from
    // Flightradar24 and trail live by about two minutes.
    id: 'bushfireio',
    name: TRACKER_SOURCE_NAME.bushfireio,
    serviceName: 'bushfire.io',
    offered: BUSHFIRE_IO_ON,
    why: BUSHFIRE_IO_ON ? null : 'Switched off: bushfire.io has not agreed to Teslyn’s use.',
    area: 'view_box',
    needsAccount: false,
    pollMs: BUSHFIRE_POLL_MS,
    minGapMs: 30_000,
    moveDebounceMs: 3_000,
    radiusNm: { default: 0, max: 0 },
    backoff: BACKOFF_MINUTES,
    trailHours: 6,
    // an aircraft quiet for over 10 minutes is dropped by the reader; two minutes behind live, so a fix is old early
    maxAgeMs: 12 * MIN,
    credit: 'Fire aircraft: © Disaster Science (bushfire.io), positions © Flightradar24',
    fallback: null,
  },
};

/** The sources a user may switch on, in the map's order. */
export const OFFERED_PUBLIC_TRAFFIC: readonly PublicTrafficId[] = (
  ['adsb', 'ozrunways', 'avplan', 'opensky', 'bushfireio'] as const
).filter((s) => PUBLIC_TRAFFIC_SOURCES[s].offered);

/** The user's public traffic choices for a project (kept with the map prefs). */
export interface PublicTrafficPrefs {
  /** The sources switched on (none by default, as the Diurnal tool). */
  readonly sources: readonly PublicTrafficId[];
  /** Nautical miles around the map centre per source; absent: the source's default. */
  readonly radius_nm: Readonly<Partial<Record<PublicTrafficId, number>>>;
}

export const DEFAULT_PUBLIC_TRAFFIC: PublicTrafficPrefs = { sources: [], radius_nm: {} };

/** Check a radius typed for a source: whole nautical miles from 1 to the source's limit. */
export function checkTrafficRadius(source: PublicTrafficId, nm: number): string | null {
  const max = PUBLIC_TRAFFIC_SOURCES[source].radiusNm.max;
  return Number.isInteger(nm) && nm >= 1 && nm <= max ? null : `Enter whole nautical miles from 1 to ${max}.`;
}

/** The radius a source uses: the user's, kept within the source's limit, else its default. */
export function trafficRadiusNm(prefs: PublicTrafficPrefs, source: PublicTrafficId): number {
  const s = PUBLIC_TRAFFIC_SOURCES[source].radiusNm;
  const v = prefs.radius_nm[source];
  return typeof v === 'number' && Number.isFinite(v)
    ? Math.min(s.max, Math.max(1, Math.round(v)))
    : s.default;
}

/** Stored choices made safe: only offered sources, once each; radii whole and within each source's limit. */
export function normalisePublicTraffic(v: unknown): PublicTrafficPrefs {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return DEFAULT_PUBLIC_TRAFFIC;
  const o = v as Record<string, unknown>;
  const sources = Array.isArray(o.sources)
    ? OFFERED_PUBLIC_TRAFFIC.filter((s) => (o.sources as unknown[]).includes(s))
    : [];
  const radius: Partial<Record<PublicTrafficId, number>> = {};
  if (o.radius_nm && typeof o.radius_nm === 'object') {
    for (const s of OFFERED_PUBLIC_TRAFFIC) {
      if (PUBLIC_TRAFFIC_SOURCES[s].area === 'view_box') continue;
      const r = (o.radius_nm as Record<string, unknown>)[s];
      if (typeof r === 'number' && checkTrafficRadius(s, Math.round(r)) === null) radius[s] = Math.round(r);
    }
  }
  return { sources, radius_nm: radius };
}

// ---------------------------------------------------------------------------------------------- distances

const EARTH_KM = 6371.0088;
const KM_PER_NM = 1.852;

/** Great-circle distance in nautical miles. */
export function nmBetween(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r;
  const dLon = (b.lon - a.lon) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLon / 2) ** 2;
  return (2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)))) / KM_PER_NM;
}

/** The centre of a map view. */
export function viewCentre(b: GeoBounds): { lat: number; lon: number } {
  let east = b.east;
  if (east < b.west) east += 360;
  let lon = (b.west + east) / 2;
  if (lon > 180) lon -= 360;
  return { lat: (b.south + b.north) / 2, lon };
}

const round2 = (v: number): number => Math.round(v * 100) / 100;
const clampTo = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** The box OpenSky is asked for, and whether the view was larger than one credit allows. */
export interface OpenSkyBox {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
  readonly clamped: boolean;
}

/**
 * The OpenSky box for a map view (am:5908–5936): the view itself, to 2 decimals, when it is 25 square degrees or
 * less (one credit); otherwise 5 x 5 degrees around the view's centre. Kept on the globe; a view across the
 * antimeridian is cut at 180°.
 */
export function openSkyBox(view: GeoBounds): OpenSkyBox {
  const south = clampTo(Math.min(view.south, view.north), -90, 90);
  const north = clampTo(Math.max(view.south, view.north), -90, 90);
  const west = clampTo(view.west, -180, 180);
  const east = view.east < view.west ? 180 : clampTo(view.east, -180, 180);
  const b = { south: round2(south), west: round2(west), north: round2(north), east: round2(east) };
  if ((b.north - b.south) * (b.east - b.west) <= OPENSKY_ONE_CREDIT_SQ_DEG) return { ...b, clamped: false };
  const c = viewCentre(view);
  const half = OPENSKY_CLAMP_DEG / 2;
  const lat0 = clampTo(round2(c.lat - half), -90, 90 - OPENSKY_CLAMP_DEG);
  const lon0 = clampTo(round2(c.lon - half), -180, 180 - OPENSKY_CLAMP_DEG);
  return {
    south: lat0,
    west: lon0,
    north: round2(lat0 + OPENSKY_CLAMP_DEG),
    east: round2(lon0 + OPENSKY_CLAMP_DEG),
    clamped: true,
  };
}

/** The box asked for a source: OpenSky's one-credit box; bushfire.io the view itself (its tiles cover it). */
function boxFor(source: PublicTrafficId, view: GeoBounds): OpenSkyBox {
  if (source !== 'bushfireio') return openSkyBox(view);
  const south = clampTo(Math.min(view.south, view.north), -85, 85);
  const north = clampTo(Math.max(view.south, view.north), -85, 85);
  const west = clampTo(view.west, -180, 180);
  const east = view.east < view.west ? 180 : clampTo(view.east, -180, 180);
  return {
    south: round4b(south),
    west: round4b(west),
    north: round4b(north),
    east: round4b(east),
    clamped: false,
  };
}
const round4b = (v: number): number => Math.round(v * 10_000) / 10_000;

const boxInside = (inner: OpenSkyBox, outer: OpenSkyBox): boolean =>
  inner.south >= outer.south &&
  inner.north <= outer.north &&
  inner.west >= outer.west &&
  inner.east <= outer.east;

// ---------------------------------------------------------------------------------------------- readers

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

const publicBase = (source: PublicTrafficId) =>
  ({
    source,
    account: '',
    fleetGroup: '',
    sos: false,
    satellite: null,
    tripId: null,
  }) as const;

/**
 * adsb.lol `/v2/point/{lat}/{lon}/{radius}` (the readsb aircraft list, am:8146–8189): `now` (ms) and `ac[]` with
 * `hex`, `flight`, `r` (registration), `t` (ICAO type), `category`, `lat`, `lon`, `alt_baro` (feet, or "ground"),
 * `alt_geom`, `gs` (knots), `track`, `seen_pos` (seconds since the position). A hex starting with "~" is not an ICAO
 * address (TIS-B), so it identifies nothing beyond this feed. adsb.fi's `/api/v3/lat/…/lon/…/dist/…` answers the same
 * list (checked on one live answer of 20 Oct 2026, not kept: its terms); the Diurnal tool also read an `aircraft`
 * list from adsb.fi's old v2 (am:7960), so that name is read too. `service` names it in the problems.
 */
export function parseAdsbLol(raw: string, service = 'adsb.lol'): Parsed<TrackedPosition[]> {
  let data: Record<string, unknown>;
  try {
    data = obj(JSON.parse(raw));
  } catch {
    return { value: [], problem: `The ${service} answer is not JSON.` };
  }
  const list = Array.isArray(data.ac) ? data.ac : Array.isArray(data.aircraft) ? data.aircraft : null;
  const now = num(data.now);
  if (!list || now === null) return { value: [], problem: `The ${service} answer has no aircraft list.` };
  const out: TrackedPosition[] = [];
  for (const raw1 of list) {
    const a = obj(raw1);
    const lat = num(a.lat);
    const lon = num(a.lon);
    const hexRaw = str(a.hex).toLowerCase();
    if (lat === null || lon === null || !hexRaw) continue;
    const seen = num(a.seen_pos) ?? num(a.seen) ?? 0;
    const timeMs = Math.round(now - seen * 1000);
    const ground = a.alt_baro === 'ground';
    const callsign = str(a.flight);
    const rego = str(a.r);
    const p: TrackedPosition = {
      ...publicBase('adsb'),
      deviceId: hexRaw,
      label: callsign || rego || hexRaw.toUpperCase(),
      registration: rego || null,
      icaoHex: hexRaw.startsWith('~') ? null : normaliseHex(hexRaw),
      kind: kindFromIcaoType(str(a.t), str(a.category)),
      providerFlying: ground ? false : num(a.alt_baro) !== null ? true : null,
      lat,
      lon,
      timeMs,
      altFt: ground ? null : (num(a.alt_baro) ?? num(a.alt_geom)),
      speedKt: num(a.gs),
      headingDeg: num(a.track) ?? num(a.true_heading) ?? num(a.mag_heading),
    };
    if (validFix(p)) out.push(p);
  }
  return { value: out, problem: null };
}

const FT_PER_M = 3.280839895;
const KT_PER_MS = 1.943844492;
/** OpenSky's aircraft category (`extended=1`, index 17) for rotorcraft (the ADS-B category A7). */
const OPENSKY_ROTORCRAFT = 8;

/**
 * OpenSky `/api/states/all` (openskynetwork.github.io/opensky-api/rest.html, read 20 Oct 2026; the Diurnal reader
 * am:6039–6060): `time` (s) and `states`, null when no aircraft is in the box, each an array: 0 icao24, 1 callsign
 * (padded to 8), 2 origin country, 3 time_position (s, null without a position in the last 15 s), 4 last_contact
 * (s), 5 longitude, 6 latitude, 7 baro_altitude (metres), 8 on_ground, 9 velocity (m/s), 10 true_track (degrees),
 * 11 vertical_rate, 12 sensors, 13 geo_altitude (metres), 14 squawk, 15 spi, 16 position_source, 17 category (with
 * extended=1; 8 is rotorcraft). Metres become feet and m/s knots, as the Diurnal tool converted them.
 */
export function parseOpenSky(raw: string): Parsed<TrackedPosition[]> {
  let data: Record<string, unknown>;
  try {
    data = obj(JSON.parse(raw));
  } catch {
    return { value: [], problem: 'The OpenSky answer is not JSON.' };
  }
  if (num(data.time) === null || !(data.states === null || Array.isArray(data.states)))
    return { value: [], problem: 'The OpenSky answer has no aircraft list.' };
  const out: TrackedPosition[] = [];
  for (const sv of (data.states as unknown[] | null) ?? []) {
    if (!Array.isArray(sv)) continue;
    const hex = normaliseHex(str(sv[0]));
    const lon = num(sv[5]);
    const lat = num(sv[6]);
    const t = num(sv[3]) ?? num(sv[4]);
    if (!hex || lat === null || lon === null || t === null) continue;
    const callsign = str(sv[1]);
    const ground = sv[8] === true;
    const altM = num(sv[7]) ?? num(sv[13]);
    const speed = num(sv[9]);
    const p: TrackedPosition = {
      ...publicBase('opensky'),
      deviceId: hex,
      label: callsign || hex.toUpperCase(),
      registration: null,
      icaoHex: hex,
      kind: num(sv[17]) === OPENSKY_ROTORCRAFT ? 'helicopter' : null,
      providerFlying: ground ? false : altM !== null ? true : null,
      lat,
      lon,
      timeMs: Math.round(t * 1000),
      altFt: ground || altM === null ? null : Math.round(altM * FT_PER_M),
      speedKt: speed === null ? null : Math.round(speed * KT_PER_MS * 10) / 10,
      headingDeg: num(sv[10]),
    };
    if (validFix(p)) out.push(p);
  }
  return { value: out, problem: null };
}

const OZ_ALT = /Altitude<\/dt>\s*<dd>\s*([\d,]+)\s*ft/i;
const OZ_SPEED = /Speed<\/dt>\s*<dd>\s*([\d,]+)\s*kt/i;

/**
 * Oz Runways `tx/geo` (am:9618–9671): a GeoJSON FeatureCollection of the whole world; `geometry.coordinates` [lon,
 * lat]; `properties.name` (callsign or short registration), `tk` (track key), `trk` (track), `age` (seconds since
 * the position, sent negative), `popupContent` (HTML from which only the altitude in ft and speed in kt are read, as
 * text), `sim` (a simulator: skipped), `suppressed` (the pilot asked not to be shown: skipped, where the Diurnal tool
 * drew it grey). Positions older than 12 hours are dropped (am:469).
 */
export function parseOzRunways(raw: string, fetchedMs: number): Parsed<TrackedPosition[]> {
  let data: Record<string, unknown>;
  try {
    data = obj(JSON.parse(raw));
  } catch {
    return { value: [], problem: 'The Oz Runways answer is not JSON.' };
  }
  if (!Array.isArray(data.features)) return { value: [], problem: 'The Oz Runways answer has no features.' };
  const out: TrackedPosition[] = [];
  for (const fRaw of data.features) {
    const f = obj(fRaw);
    const props = obj(f.properties);
    const coords = obj(f.geometry).coordinates;
    if (!Array.isArray(coords) || props.sim === true || props.suppressed === true) continue;
    const lon = num(coords[0]);
    const lat = num(coords[1]);
    const name = str(props.name);
    if (lat === null || lon === null) continue;
    const ageS = Math.abs(num(props.age) ?? 0);
    if (ageS * 1000 > PUBLIC_TRAFFIC_SOURCES.ozrunways.maxAgeMs) continue;
    const html = typeof props.popupContent === 'string' ? props.popupContent : '';
    const altM = OZ_ALT.exec(html);
    const spdM = OZ_SPEED.exec(html);
    const altFt = altM ? Number(altM[1]!.replace(/,/g, '')) : null;
    const speedKt = spdM ? Number(spdM[1]!.replace(/,/g, '')) : null;
    const label = name && name.toLowerCase() !== 'unknown' ? name : str(props.tk) || 'Oz Runways';
    const p: TrackedPosition = {
      ...publicBase('ozrunways'),
      deviceId: str(props.tk) || name,
      label,
      registration: null,
      icaoHex: null,
      kind: null,
      // the Diurnal rule (am:9666): no altitude and no speed is on the ground
      providerFlying: !altFt && !speedKt ? false : null,
      lat,
      lon,
      timeMs: Math.round(fetchedMs - ageS * 1000),
      altFt,
      speedKt,
      headingDeg: num(props.trk),
    };
    if (p.deviceId && validFix(p)) out.push(p);
  }
  return { value: out, problem: null };
}

/**
 * AvPlan EFB `live.json` (am:9065–9103): an object of entries `{callsign, udid, latitude, longitude, heading,
 * altitude (ft), groundspeed (kt), timestamp (s)}`; spaces removed from the callsign ("ZK RNT" → "ZKRNT"). The
 * callsign identifies the aircraft (the device id only when there is no callsign, so a person's device id is not
 * kept). Positions older than 12 hours are dropped.
 */
export function parseAvPlan(raw: string, fetchedMs: number): Parsed<TrackedPosition[]> {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { value: [], problem: 'The AvPlan answer is not JSON.' };
  }
  if (data === null) return { value: [], problem: null };
  if (!data || typeof data !== 'object' || Array.isArray(data))
    return { value: [], problem: 'The AvPlan answer has no aircraft list.' };
  const out: TrackedPosition[] = [];
  for (const eRaw of Object.values(data as Record<string, unknown>)) {
    const e = obj(eRaw);
    const lat = num(e.latitude);
    const lon = num(e.longitude);
    const ts = num(e.timestamp);
    if (lat === null || lon === null || ts === null) continue;
    const timeMs = Math.round(ts * 1000);
    if (fetchedMs - timeMs > PUBLIC_TRAFFIC_SOURCES.avplan.maxAgeMs) continue;
    const callsign = str(e.callsign).replace(/\s+/g, '');
    const altFt = num(e.altitude);
    const speedKt = num(e.groundspeed);
    const p: TrackedPosition = {
      ...publicBase('avplan'),
      deviceId: callsign.toUpperCase() || str(e.udid),
      label: callsign || 'AvPlan',
      registration: null,
      icaoHex: null,
      kind: null,
      providerFlying: !altFt && !speedKt ? false : null,
      lat,
      lon,
      timeMs,
      altFt,
      speedKt,
      headingDeg: num(e.heading),
    };
    if (p.deviceId && validFix(p)) out.push(p);
  }
  return { value: out, problem: null };
}

// ---------------------------------------------------------------------------------------------- waiting

/** A Retry-After value (seconds or an HTTP date) as milliseconds from now; null when absent or unreadable. */
export function retryAfterMs(value: string | null | undefined, nowMs: number): number | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  if (/^\d+$/.test(v)) return Number(v) * 1000;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : Math.max(0, t - nowMs);
}

/**
 * How long a source waits after a refusal (am:8023–8037): too many requests (429) or refused (403) wait 1, 2, 4 …
 * minutes up to 15, a refusal at least 5 minutes; OpenSky's 429 waits 30, 60, 120 then 240 s (am:6032–6036). The
 * service's own Retry-After (OpenSky: X-Rate-Limit-Retry-After-Seconds) wins when it says, up to a day. Other
 * failures wait one poll interval.
 */
export function trafficCooldownMs(
  status: number,
  consecutiveFails: number,
  retryAfter: number | null,
  pollMs: number,
  backoff: { readonly firstMs: number; readonly maxMs: number } = BACKOFF_MINUTES,
): number {
  if (status !== 429 && status !== 403) return pollMs;
  if (retryAfter !== null) return Math.min(Math.max(retryAfter, 1000), 24 * HOUR);
  const exp = Math.min(backoff.firstMs * 2 ** Math.max(0, consecutiveFails - 1), backoff.maxMs);
  return status === 403 ? Math.max(5 * MIN, exp) : exp;
}

// ---------------------------------------------------------------------------------------------- the feed

/** One request of Teslyn Field for a source (the app builds the address; nothing else can be asked). */
export interface PublicTrafficRequest {
  readonly source: PublicTrafficId;
  /** The fallback service asked instead (Community ADS-B: adsb.fi). */
  readonly via?: 'adsbfi';
  readonly lat?: number;
  readonly lon?: number;
  readonly radius_nm?: number;
  /** OpenSky's box, degrees. */
  readonly south?: number;
  readonly west?: number;
  readonly north?: number;
  readonly east?: number;
  /** OpenSky's access token from Teslyn Cloud (never stored; only OpenSky's request carries it). */
  readonly bearer?: string;
}

export interface PublicTrafficAnswer {
  readonly status: number;
  readonly body: string;
  readonly fetchedAt: string;
  /** Another map window asked for the same a moment ago. */
  readonly cached: boolean;
  readonly retryAfter: string | null;
  /** Credits the account has left today, when the service says (OpenSky's X-Rate-Limit-Remaining). */
  readonly creditsRemaining?: string | null;
}

/** Teslyn Field's fetcher (`public_traffic_fetch`); it throws when the service cannot be reached. */
export interface PublicTrafficFetcher {
  fetch(req: PublicTrafficRequest): Promise<PublicTrafficAnswer>;
}

/** An OpenSky access token from Teslyn Cloud, with when it ends. */
export interface OpenSkyToken {
  readonly token: string;
  readonly expiresAtMs: number;
}

/**
 * Where the feed gets OpenSky's token: the provider proxy's `opensky` `token` operation with the signed-in user's
 * Teslyn token. It throws an error with a `code` ("not_set_up", "signed_out", "credential_refused", "forbidden",
 * "offline", "rate_limited", "provider_error") and words. `renew` asks for a new one (OpenSky refused the last).
 */
export interface OpenSkyTokenSource {
  get(renew: boolean): Promise<OpenSkyToken>;
}

/**
 * Whether the project's organisation may use adsb.fi (decision 405): a switch per organisation in Teslyn Cloud
 * (`tenant_service_switch`, set by Braynewave's platform admins), on for Thomson Airborne, off for the others until
 * each has its own arrangement with adsb.fi. Unknown (offline, never read) is off.
 */
export type AdsbFiSetUp = 'on' | 'off' | 'no_organisation' | 'signed_out' | 'unknown';

export const adsbFiAllowed = (s: AdsbFiSetUp): boolean => s === 'on';

/** The words beside Community ADS-B for adsb.fi's switch; null when it is on (or not known yet). */
export function adsbFiSetUpText(s: AdsbFiSetUp): string | null {
  switch (s) {
    case 'off':
      return 'adsb.fi (when adsb.lol cannot answer): not set up for your organisation. Braynewave switches it on once your organisation has an arrangement with adsb.fi.';
    case 'no_organisation':
      return 'adsb.fi (when adsb.lol cannot answer): off, as this project has no organisation.';
    case 'signed_out':
      return 'adsb.fi (when adsb.lol cannot answer): sign in to Teslyn to use your organisation’s arrangement.';
    default:
      return null;
  }
}

/** bushfire.io's words when the organisation's switch is off (decision 476). */
export const BUSHFIRE_IO_OFF =
  'Not set up for your organisation: bushfire.io’s terms need an agreement, and Braynewave switches it on per organisation.';

/** A status's words when adsb.fi would have been asked but is off for the organisation. */
export const ADSBFI_OFF = 'adsb.fi is not set up for your organisation.';

/**
 * Whether the project's organisation has an OpenSky API client in Teslyn Cloud, Integrations (its metadata, never
 * the value): shown beside OpenSky in the map's Layers panel before anyone ticks it.
 */
export type OpenSkySetUp = 'set_up' | 'failing' | 'not_set_up' | 'no_organisation' | 'signed_out' | 'unknown';

/** The words beside OpenSky for its set-up; null when it is set up (or not known, offline). */
export function openSkySetUpText(s: OpenSkySetUp): string | null {
  switch (s) {
    case 'not_set_up':
      return 'Not set up: a tenant admin adds your organisation’s OpenSky API client in Teslyn Cloud, Integrations.';
    case 'no_organisation':
      return 'Not set up: OpenSky uses the API client of the project’s organisation, and this project has none.';
    case 'signed_out':
      return 'Not set up on this laptop: sign in to Teslyn to use your organisation’s OpenSky API client.';
    case 'failing':
      return 'Not working: OpenSky refused the stored API client. A tenant admin replaces it in Teslyn Cloud, Integrations.';
    default:
      return null;
  }
}

/** A token is used until a minute before it ends (OpenSky's last 30 minutes, the proxy hands out at least 5). */
const TOKEN_MARGIN_MS = MIN;

export type PublicSourceState = 'waiting' | 'ok' | 'waiting_out' | 'offline' | 'error' | 'not_set_up';

export interface PublicSourceStatus {
  readonly source: PublicTrafficId;
  readonly state: PublicSourceState;
  readonly ok: boolean;
  /** "Community ADS-B: 12 aircraft within 100 nm, updated 10:42". */
  readonly message: string;
  /** The credit of the service whose positions are shown (adsb.lol or adsb.fi for Community ADS-B). */
  readonly credit: string;
  /** OpenSky's credits left today, as its last answer said; null when not known (status bar, 05-013). */
  readonly credits?: number | null;
}

/**
 * Which map window asks (decision 378, Q301 answer B): only the window in use, while the others keep the last answer
 * (`active_window`); `each_window` (every window for its own view) was the behaviour before. Answer C, one request
 * covering every window, is `sharedTrafficRequest`, not used.
 */
export type TrafficWindowPolicy = 'each_window' | 'active_window';
export const TRAFFIC_WINDOW_POLICY: TrafficWindowPolicy = 'active_window';

/** What a map window knows of itself, for `windowAsks`. */
export interface WindowActivity {
  /** The window is shown (not minimised or hidden behind its own tab). */
  readonly visible: boolean;
  /** The window has the keyboard focus. */
  readonly focused: boolean;
  /** No map window has the focus now and this one had it last (Teslyn is behind another program). */
  readonly lastFocused: boolean;
}

/**
 * Decision 378: the map window being looked at asks the services. A visible window with the focus asks; when no map
 * window has the focus (another program is in front), the visible window that had it last keeps asking, so a map
 * left on a second screen stays current. A hidden or minimised window never asks.
 */
export function windowAsks(w: WindowActivity): boolean {
  return w.visible && (w.focused || w.lastFocused);
}

/** The words a window that is not asking adds to each source's status. */
export const WINDOW_NOT_ASKING = 'Asked by the map window in use; this window shows its last answer.';

export interface PublicTrafficFeed {
  setPrefs(prefs: PublicTrafficPrefs): void;
  setView(view: GeoBounds): void;
  /** Ask every source that is due; true when any answer arrived. */
  poll(): Promise<boolean>;
  /** The positions of every source switched on, inside its radius of the map centre (and those `setKeep` keeps). */
  latest(): TrackedPosition[];
  /** Every position of every source switched on, wherever it is (auto-pin, 03-042: the whole Oz Runways feed). */
  everywhere(): TrackedPosition[];
  /** Positions kept whatever the radius (pinned aircraft, 03-041: am:9819 "pinned bypass the radius"). */
  setKeep(keep: ((p: TrackedPosition) => boolean) | null): void;
  status(): PublicSourceStatus[];
}

interface ServiceState {
  fails: number;
  retryAt: number | null;
  lastAt: number;
  problem: string | null;
}

interface Runtime {
  readonly source: PublicTrafficSource;
  nextAt: number;
  lastRequestAt: number;
  all: TrackedPosition[];
  /** The centre and radius the last answer was asked for (ADS-B answers only around it). */
  askedFor: { lat: number; lon: number; radius: number } | null;
  /** OpenSky's last box. */
  askedBox: OpenSkyBox | null;
  state: PublicSourceState;
  lastOkAt: number | null;
  problem: string | null;
  /** No request before this (every service of the source is waiting out a refusal); null when one may be asked. */
  retryAt: number | null;
  /** The first service and the fallback, each with its own waits (am:7999–8040). */
  primary: ServiceState;
  fallback: ServiceState;
  /** Which service answered last: the credit shown. */
  answeredBy: 'primary' | 'fallback';
  token: OpenSkyToken | null;
  /** The credits the account has left today, from the last answer that said (OpenSky). */
  credits: number | null;
}

const round4 = (v: number): number => Math.round(v * 10_000) / 10_000;

function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const service = (): ServiceState => ({ fails: 0, retryAt: null, lastAt: -Infinity, problem: null });

/** The words of an OpenSky token failure (the provider proxy's codes). */
function openSkyTokenProblem(e: unknown): { state: PublicSourceState; problem: string } {
  const code = e && typeof e === 'object' && 'code' in e ? String((e as { code: unknown }).code) : '';
  const words = e instanceof Error && e.message ? e.message : 'Teslyn Cloud did not answer.';
  switch (code) {
    case 'not_set_up':
      return {
        state: 'not_set_up',
        problem:
          'Not set up. A tenant admin adds your organisation’s OpenSky API client in Teslyn Cloud, Integrations.',
      };
    case 'signed_out':
      return {
        state: 'not_set_up',
        problem: 'Sign in to Teslyn to use your organisation’s OpenSky API client.',
      };
    case 'credential_refused':
      return {
        state: 'error',
        problem:
          'OpenSky refused the stored API client. A tenant admin replaces it in Teslyn Cloud, Integrations.',
      };
    case 'forbidden':
      return { state: 'error', problem: 'You cannot use OpenSky here.' };
    case 'offline':
      return { state: 'offline', problem: 'Teslyn Cloud cannot be reached for the OpenSky token.' };
    default:
      return { state: 'error', problem: words };
  }
}

export function createPublicTraffic(opts: {
  readonly fetcher: PublicTrafficFetcher;
  readonly now: () => number;
  readonly prefs?: PublicTrafficPrefs;
  /** OpenSky's token from Teslyn Cloud; none (no organisation): OpenSky says it is not set up. */
  readonly openSky?: OpenSkyTokenSource | null;
  /** Q301: which window asks (each window by default). */
  readonly policy?: TrafficWindowPolicy;
  /** With the `active_window` policy: whether this window is the one in use. */
  readonly isActiveWindow?: () => boolean;
  /** Decision 405: whether the organisation may use adsb.fi (its switch); none: allowed. */
  readonly adsbFi?: () => boolean;
  /** Decision 476: whether the organisation may use bushfire.io (its switch); none: not allowed. */
  readonly bushfireIo?: () => boolean;
}): PublicTrafficFeed {
  let prefs = normalisePublicTraffic(opts.prefs ?? DEFAULT_PUBLIC_TRAFFIC);
  let view: GeoBounds | null = null;
  let centre: { lat: number; lon: number } | null = null;
  let keep: ((p: TrackedPosition) => boolean) | null = null;
  const policy = opts.policy ?? TRAFFIC_WINDOW_POLICY;
  const runtimes = new Map<PublicTrafficId, Runtime>();
  const runtime = (id: PublicTrafficId): Runtime => {
    let r = runtimes.get(id);
    if (!r) {
      r = {
        source: PUBLIC_TRAFFIC_SOURCES[id],
        nextAt: 0,
        lastRequestAt: -Infinity,
        all: [],
        askedFor: null,
        askedBox: null,
        state: 'waiting',
        lastOkAt: null,
        problem: null,
        retryAt: null,
        primary: service(),
        fallback: service(),
        answeredBy: 'primary',
        token: null,
        credits: null,
      };
      runtimes.set(id, r);
    }
    return r;
  };
  const on = (): Runtime[] => prefs.sources.map(runtime);

  /** A map move or a new radius: ask again soon, never sooner than the source's least gap. */
  function moved(r: Runtime, now: number): void {
    if (r.source.moveDebounceMs === 0 || (r.retryAt !== null && now < r.retryAt)) return;
    const asked = r.source.area === 'view_box' ? r.askedBox : r.askedFor;
    if (!asked) {
      // the first view: ask now (within the least gap)
      r.nextAt = Math.max(Math.min(r.nextAt, now), r.lastRequestAt + r.source.minGapMs);
      return;
    }
    if (r.source.area === 'view_box') {
      // a view still inside the box asked costs no credit
      if (view && r.askedBox && boxInside(boxFor(r.source.id, view), r.askedBox)) return;
    } else {
      const radius = trafficRadiusNm(prefs, r.source.id);
      const a = r.askedFor!;
      if (centre && a.radius === radius && nmBetween(a, centre) < radius / 4) return;
    }
    r.nextAt = Math.max(now + r.source.moveDebounceMs, r.lastRequestAt + r.source.minGapMs);
  }

  /** The request for a source now; null when there is nothing to ask around yet. */
  function requestFor(r: Runtime): PublicTrafficRequest | null {
    const s = r.source;
    if (s.area === 'radius') {
      if (!centre) return null;
      return {
        source: s.id,
        lat: round4(centre.lat),
        lon: round4(centre.lon),
        radius_nm: trafficRadiusNm(prefs, s.id),
      };
    }
    if (s.area === 'view_box') {
      if (!view) return null;
      const b = boxFor(s.id, view);
      return { source: s.id, south: b.south, west: b.west, north: b.north, east: b.east };
    }
    return { source: s.id };
  }

  function parse(r: Runtime, body: string, fetchedMs: number, via: 'primary' | 'fallback') {
    switch (r.source.id) {
      case 'adsb':
        return parseAdsbLol(body, via === 'fallback' ? r.source.fallback!.name : r.source.serviceName);
      case 'ozrunways':
        return parseOzRunways(body, fetchedMs);
      case 'avplan':
        return parseAvPlan(body, fetchedMs);
      case 'opensky':
        return parseOpenSky(body);
      case 'bushfireio':
        return parseBushfireAircraft(body, fetchedMs);
    }
  }

  /** One service's answer: the positions, or why not (the service's wait set on a 429 or 403). */
  async function askService(
    r: Runtime,
    req: PublicTrafficRequest,
    which: 'primary' | 'fallback',
    now: number,
  ): Promise<
    | { ok: true; value: TrackedPosition[]; fetchedMs: number }
    | { ok: false; offline: boolean; status: number | null }
  > {
    const s = r.source;
    const st = which === 'primary' ? r.primary : r.fallback;
    const name = which === 'primary' ? s.serviceName : s.fallback!.name;
    st.lastAt = now;
    let a: PublicTrafficAnswer;
    try {
      a = await opts.fetcher.fetch(req);
      // OpenSky: an ended token is renewed once and the request asked again (its docs: 401, then a new token)
      if (a.status === 401 && s.id === 'opensky' && opts.openSky) {
        r.token = await opts.openSky.get(true);
        a = await opts.fetcher.fetch({ ...req, bearer: r.token.token });
      }
    } catch (e) {
      const t =
        s.id === 'opensky' && e && typeof e === 'object' && 'code' in e ? openSkyTokenProblem(e) : null;
      st.problem = t ? t.problem : e instanceof Error && e.message ? e.message : `${name} cannot be reached.`;
      return { ok: false, offline: t ? t.state === 'offline' : true, status: null };
    }
    const fetchedMs = Date.parse(a.fetchedAt) || now;
    if (a.creditsRemaining && /^\d{1,7}$/.test(a.creditsRemaining)) r.credits = Number(a.creditsRemaining);
    if (a.status !== 200) {
      st.fails += 1;
      const wait = trafficCooldownMs(
        a.status,
        st.fails,
        retryAfterMs(a.retryAfter, now),
        s.pollMs,
        s.backoff,
      );
      st.retryAt = a.status === 429 || a.status === 403 ? now + wait : null;
      st.problem =
        a.status === 429
          ? `${name} asks Teslyn to wait (too many requests); trying again at ${hhmm(now + wait)}.`
          : a.status === 403
            ? `${name} refused the request; trying again at ${hhmm(now + wait)}.`
            : a.status === 401 && s.id === 'opensky'
              ? 'OpenSky refused the access token from Teslyn Cloud.'
              : `${name} answered HTTP ${a.status}.`;
      return { ok: false, offline: false, status: a.status };
    }
    const parsed = parse(r, a.body, fetchedMs, which);
    if (parsed.problem) {
      st.problem = parsed.problem;
      return { ok: false, offline: false, status: 200 };
    }
    st.fails = 0;
    st.retryAt = null;
    st.problem = null;
    return { ok: true, value: parsed.value, fetchedMs };
  }

  async function ask(r: Runtime, now: number): Promise<boolean> {
    const s = r.source;
    const base = requestFor(r);
    if (!base) return false; // nothing to ask around until the map shows
    r.lastRequestAt = now;
    r.nextAt = now + s.pollMs;
    // decision 476: bushfire.io only for an organisation whose switch is on (Thomson)
    if (s.id === 'bushfireio' && !(opts.bushfireIo?.() ?? false)) {
      r.state = 'not_set_up';
      r.problem = BUSHFIRE_IO_OFF;
      return false;
    }
    let req = base;
    if (s.needsAccount) {
      if (!opts.openSky) {
        r.state = 'not_set_up';
        r.problem =
          'Not set up. OpenSky uses the API client of the project’s organisation, added in Teslyn Cloud, Integrations.';
        return false;
      }
      try {
        if (!r.token || r.token.expiresAtMs - now < TOKEN_MARGIN_MS) r.token = await opts.openSky.get(false);
      } catch (e) {
        r.token = null;
        const t = openSkyTokenProblem(e);
        r.state = t.state;
        r.problem = t.problem;
        return false;
      }
      req = { ...base, bearer: r.token.token };
    }
    // decision 405: adsb.fi only for an organisation whose switch is on
    const fallbackOff = s.fallback !== null && opts.adsbFi !== undefined && !opts.adsbFi();
    const services: { which: 'primary' | 'fallback'; req: PublicTrafficRequest }[] = [
      { which: 'primary', req },
      ...(s.fallback && !fallbackOff
        ? [{ which: 'fallback' as const, req: { ...req, via: s.fallback.via } }]
        : []),
    ];
    const problems: string[] = [];
    let offline = 0;
    let tried = 0;
    for (const { which, req: one } of services) {
      const st = which === 'primary' ? r.primary : r.fallback;
      if (st.retryAt !== null && now < st.retryAt) {
        if (st.problem) problems.push(st.problem);
        continue;
      }
      // adsb.fi allows one request a second; the feed asks it at most once a poll, and the app spaces windows
      if (which === 'fallback' && now - st.lastAt < s.fallback!.minSpacingMs) continue;
      tried += 1;
      const got = await askService(r, one, which, now);
      if (got.ok) {
        r.all = got.value;
        r.answeredBy = which;
        r.askedFor =
          one.lat !== undefined && one.lon !== undefined
            ? { lat: one.lat, lon: one.lon, radius: one.radius_nm ?? 0 }
            : null;
        r.askedBox = s.area === 'view_box' && view ? boxFor(s.id, view) : null;
        r.retryAt = null;
        r.state = 'ok';
        // the fallback answered: say why the first service did not
        r.problem = which === 'fallback' ? problems.join(' ') || null : null;
        r.lastOkAt = got.fetchedMs;
        return true;
      }
      if (got.offline) offline += 1;
      if (st.problem) problems.push(st.problem);
    }
    // nobody answered: wait for the first service free again, at most a poll interval
    const waits = services
      .map(({ which }) => (which === 'primary' ? r.primary : r.fallback).retryAt)
      .filter((t): t is number => t !== null && t > now);
    const allWaiting = waits.length === services.length;
    r.retryAt = allWaiting ? Math.min(...waits) : null;
    r.nextAt = allWaiting ? Math.min(...waits) : Math.min(now + s.pollMs, ...waits);
    r.state = allWaiting ? 'waiting_out' : tried > 0 && offline === tried ? 'offline' : 'error';
    if (fallbackOff) problems.push(ADSBFI_OFF);
    r.problem = problems.join(' ') || `${s.name}: no answer.`;
    return false;
  }

  function inside(r: Runtime): TrackedPosition[] {
    if (r.source.area === 'view_box') return r.all;
    const radius = trafficRadiusNm(prefs, r.source.id);
    const c = r.source.area === 'radius' ? (r.askedFor ?? centre) : centre;
    if (!c) return keep ? r.all.filter(keep) : [];
    return r.all.filter((p) => nmBetween(c, p) <= radius || (keep !== null && keep(p)));
  }

  function okMessage(r: Runtime, n: number): string {
    const s = r.source;
    const when = hhmm(r.lastOkAt!);
    if (s.area === 'view_box') {
      const where = r.askedBox?.clamped
        ? `in ${OPENSKY_CLAMP_DEG}° × ${OPENSKY_CLAMP_DEG}° around the map centre`
        : 'in the map view';
      return `${s.name}: ${n} aircraft ${where}, updated ${when}`;
    }
    const radius = trafficRadiusNm(prefs, s.id);
    const from = r.answeredBy === 'fallback' && s.fallback ? ` from ${s.fallback.name}` : '';
    const why = r.answeredBy === 'fallback' && r.problem ? ` (${r.problem.replace(/\.$/, '')})` : '';
    return `${s.name}: ${n} aircraft within ${radius} nm, updated ${when}${from}${why}`;
  }

  return {
    setPrefs(p) {
      const before = prefs;
      prefs = normalisePublicTraffic(p);
      const now = opts.now();
      for (const id of prefs.sources) {
        const r = runtime(id);
        if (!before.sources.includes(id))
          r.nextAt = Math.max(now, r.retryAt ?? 0, r.lastRequestAt + r.source.minGapMs);
        else if (trafficRadiusNm(prefs, id) !== trafficRadiusNm(before, id)) moved(r, now);
      }
    },
    setView(v) {
      view = v;
      centre = viewCentre(v);
      const now = opts.now();
      for (const r of on()) moved(r, now);
    },
    async poll() {
      // Q301 answer B: a window not in use keeps its last answer and asks nothing
      if (policy === 'active_window' && opts.isActiveWindow && !opts.isActiveWindow()) return false;
      const now = opts.now();
      const due = on().filter((r) => r.source.offered && now >= r.nextAt);
      const got = await Promise.all(due.map((r) => ask(r, now)));
      return got.some(Boolean);
    },
    latest() {
      return on()
        .filter((r) => r.source.offered)
        .flatMap((r) => inside(r));
    },
    everywhere() {
      return on()
        .filter((r) => r.source.offered)
        .flatMap((r) => r.all);
    },
    setKeep(k) {
      keep = k;
    },
    status() {
      return on().map((r) => {
        const s = r.source;
        const n = r.state === 'ok' || r.lastOkAt !== null ? inside(r).length : 0;
        const kept = r.lastOkAt !== null ? ` Showing the positions of ${hhmm(r.lastOkAt)}.` : '';
        const message =
          r.state === 'ok' && r.lastOkAt !== null
            ? okMessage(r, n)
            : r.state === 'waiting'
              ? `${s.name}: ${requestFor(r) === null ? 'waiting for the map' : 'connecting…'}`
              : `${s.name}: ${r.problem ?? 'no answer.'}${kept}`;
        const credit = r.answeredBy === 'fallback' && s.fallback ? s.fallback.credit : s.credit;
        const idle = policy === 'active_window' && opts.isActiveWindow && !opts.isActiveWindow();
        return {
          source: s.id,
          state: r.state,
          ok: r.state === 'ok',
          message: idle ? `${message.replace(/\.?$/, '.')} ${WINDOW_NOT_ASKING}` : message,
          credit,
          credits: r.credits,
        };
      });
    },
  };
}

/**
 * Q301 answer C: one request covering what several map windows ask of a source, so the windows share one answer.
 * Community ADS-B: the smallest circle around every window's circle, if it is within the service's 250 nm; OpenSky:
 * the box around every window's box, if it is still one credit (25 square degrees); a whole-world source: one
 * request. Null when one request cannot cover them (each window then asks its own), or for different sources.
 */
export function sharedTrafficRequest(reqs: readonly PublicTrafficRequest[]): PublicTrafficRequest | null {
  const first = reqs[0];
  if (!first || reqs.some((r) => r.source !== first.source || r.via !== first.via)) return null;
  if (reqs.length === 1) return first;
  const s = PUBLIC_TRAFFIC_SOURCES[first.source];
  if (s.area === 'world') return { source: first.source };
  if (s.area === 'view_box') {
    if (
      reqs.some(
        (r) => r.south === undefined || r.west === undefined || r.north === undefined || r.east === undefined,
      )
    )
      return null;
    const south = Math.min(...reqs.map((r) => r.south!));
    const north = Math.max(...reqs.map((r) => r.north!));
    const west = Math.min(...reqs.map((r) => r.west!));
    const east = Math.max(...reqs.map((r) => r.east!));
    if ((north - south) * (east - west) > OPENSKY_ONE_CREDIT_SQ_DEG) return null;
    return { source: first.source, south, west, north, east };
  }
  // circles: grow one circle to cover each in turn (on the sphere, by the great-circle distance)
  const circles = reqs.map((r) => ({ lat: r.lat, lon: r.lon, radius: r.radius_nm }));
  if (circles.some((c) => c.lat === undefined || c.lon === undefined || c.radius === undefined)) return null;
  let c = { lat: circles[0]!.lat!, lon: circles[0]!.lon!, radius: circles[0]!.radius! };
  for (const o of circles.slice(1)) {
    const other = { lat: o.lat!, lon: o.lon!, radius: o.radius! };
    const d = nmBetween(c, other);
    if (d + other.radius <= c.radius) continue;
    if (d + c.radius <= other.radius) {
      c = other;
      continue;
    }
    const radius = (d + c.radius + other.radius) / 2;
    // the new centre lies on the line between the centres, (radius - c.radius) from c
    const f = d === 0 ? 0 : (radius - c.radius) / d;
    c = {
      lat: c.lat + (other.lat - c.lat) * f,
      lon: c.lon + (other.lon - c.lon) * f,
      radius,
    };
  }
  // the centre was moved along a straight line in degrees: measure what it really needs to cover every circle
  const radius = Math.ceil(
    Math.max(...circles.map((o) => nmBetween(c, { lat: o.lat!, lon: o.lon! }) + o.radius!)) - 1e-9,
  );
  if (radius > s.radiusNm.max) return null;
  return { source: first.source, lat: round4(c.lat), lon: round4(c.lon), radius_nm: radius };
}

/** A public position as a trail point: only while moving (the Diurnal stores, am:6121, 8242, 9819). */
export function publicTrailPoint(p: TrackedPosition): TrackPoint | null {
  if (p.providerFlying === false || (p.speedKt ?? 0) < 5) return null;
  return {
    lat: p.lat,
    lon: p.lon,
    timeMs: p.timeMs,
    altFt: p.altFt,
    speedKt: p.speedKt,
    headingDeg: p.headingDeg,
  };
}
